import React from 'react';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import Select from 'react-select';
import Layout from '../../components/layout/Layout';
import Constants from '../../config/constants';
import DataTable from '../../components/datatable/DataTable';
import Thumbnail from '../../components/thumbnail/Thumbnail';
import Alert from '../../modal/alert/Alert';
import ACPackagedUnitCreateModal from '../../modal/ac_packaged_unit/ACPackagedUnitCreateModal';
import ACPackagedUnitEditModal from '../../modal/ac_packaged_unit/ACPackagedUnitEditModal';
import ACPackagedUnitDuplicateModal from '../../modal/duplicate/ACPackagedUnitDuplicateModal';
import { COLUMN_STYLE, getColumnStyle } from '../../components/datatable/util';
import {
  defaultRejectHandler,
  getErrorValue,
  getStatusStyle,
  fromCoolingCap,
  withDefaultSort,
} from '../../utils/utility';
import {
  listACPackagedUnit,
  deleteACPackagedUnit,
  clearErrorACPackagedUnit,
} from '../../redux/ac_packaged_unit/ac_packaged_unit_actions';

export class ACPackagedUnitPage extends React.Component {
  constructor(props) {
    super(props);
    const { t } = props;

    this.search = {
      pageSize: Constants.DATA_TABLE_PAGE_SIZE,
      page: null,
      sorted: null,
      filtered: null,
    };

    const textColumn = getColumnStyle(COLUMN_STYLE.text);
    const coeColumn = getColumnStyle(COLUMN_STYLE.coefficient);

    this.columns = [
      {
        Header: '',
        accessor: 'thumbnail',
        Cell: row => <Thumbnail img={row.value}/>,
        ...getColumnStyle(COLUMN_STYLE.thumbnail),
      },
      {
        Header: () => <b>{t('name')}</b>,
        accessor: 'name',
        ...textColumn,
      },
      {
        Header: () => <b>{t('category')}</b>,
        accessor: 'is_public',
        Cell: row => row.value
          ? <span><i className="fas fa-users"/> {t('public')}</span>
          : <span><i className="fas fa-user"/> {t('private')}</span>,
        ...getColumnStyle(COLUMN_STYLE.category),
      },
      {
        Header: () => <b>{t('air type')}</b>,
        accessor: 'type',
        ...textColumn,
        width: 100,
      },
      {
        Header: () => <b>{t('brand')}</b>,
        accessor: 'brand_name',
        ...textColumn,
        width: 100,
      },
      {
        id: 'cooling_capacity',
        Header: () => <b>{t('cooling capacity')}</b>,
        accessor: data => fromCoolingCap(data.cooling_capacity, data.unit),
        ...coeColumn,
        width: 100,
      },
      {
        Header: () => <b>{t('unit')}</b>,
        accessor: 'unit',
        Cell: row => row.value === 'KBTU' ? 'BTU' : row.value,
        ...textColumn,
        width: 80,
      },
      {
        Header: () => <b>{t('power (kW)')}</b>,
        accessor: 'power_required',
        ...coeColumn,
        width: 100,
      },
      {
        Header: () => <b>{t('COP')}</b>,
        accessor: 'COP',
        ...coeColumn,
        width: 100,
      },
      {
        Header: () => <b>{t('status')}</b>,
        accessor: 'status',
        Cell: row => <span className={getStatusStyle(row.value)}>{t(String(row.value).toLowerCase())}</span>,
        width: 80,
        ...textColumn,
      },
    ];

    this.state = {
      selectedItem: null,
      categoryOption: 'all',
      isShowCreateModal: false,
      isShowEditModal: false,
      isShowDeleteModal: false,
      isShowDuplicateModal: false,
      isCreated: false,
      isEdited: false,
      isDuplicated: false,
    };
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.isCreating && nextProps.isCreating !== this.props.isCreating && !nextProps.errorMessage) {
      this.setState({ isShowCreateModal: false, isCreated: true });
    }

    if (this.props.isEditing && nextProps.isEditing !== this.props.isEditing && !nextProps.errorMessage) {
      this.setState({ isShowEditModal: false, isEdited: true });
    }

    if (this.props.isDeleting && nextProps.isDeleting !== this.props.isDeleting && !nextProps.errorMessage) {
      this.setState({ isShowDeleteModal: false });
    }

    if (this.props.isDuplicating && nextProps.isDuplicating !== this.props.isDuplicating && !nextProps.errorMessage) {
      this.setState({ isShowDuplicateModal: false, isDuplicated: true });
    }
  }

  renderActionMenu() {
    return [
      <li>
        <a
          href="#"
          id="air-conditioning-package-unit-create"
          data-toggle="modal"
          data-target="#create-ac-packaged-unit"
          onClick={() => this.setState({ isShowCreateModal: true, isCreated: false })}
        >
          <i className="fas fa-plus-square" />
          <span className="menu-text">{this.props.t('create a/c packaged unit')}</span>
        </a>
      </li>,
    ];
  }

  updateSortFilter(filter, state, category) {
    if (state) {
      this.search.pageSize = state.pageSize;
      this.search.page = state.page;
      this.search.sorted = state.sorted;
    }
    this.search.filtered = filter;

    this.props.listAC(
      this.search.pageSize,
      this.search.page,
      withDefaultSort(this.search.sorted),
      this.search.filtered,
      {
        all: null,
        public: true,
        private: false,
      }[category.value],
    );

    this.setState({ categoryOption: category });
  }

  dismissCreateModal() {
    this.props.clearError();
    this.setState({ isShowCreateModal: false });
  }

  dismissEditModal() {
    this.props.clearError();
    this.setState({ isShowEditModal: false });
  }

  dismissDeleteModal() {
    this.props.clearError();
    this.setState({ isShowDeleteModal: false });
  }

  dismissDuplicateModal() {
    this.props.clearError();
    this.setState({ isShowDuplicateModal: false });
  }

  render() {
    const {
      categoryOption,
      selectedItem: item,
      isShowCreateModal,
      isShowEditModal,
      isShowDeleteModal,
      isShowDuplicateModal,
    } = this.state;

    const {
      filtered,
      filteredCount,
      isLoading,
      isDeleting,
      isDuplicating,
      t,
      errorMessage,
    } = this.props;

    const breadcrumbItems = [
      { name: t('air conditioning system'), path: '/ac' },
      { name: t('packaged unit'), path: '/ac/packaged_unit' },
    ];

    const helpConfig = {
      title: t('help'),
      contentUrl: '/html/help_ac_packaged_unit.html',
    };

    return (
      <Layout
        history={this.props.history}
        breadcrumbItems={breadcrumbItems}
        renderActionMenu={() => this.renderActionMenu()}
        help={helpConfig}
      >
        <ACPackagedUnitCreateModal
          show={isShowCreateModal}
          isCreated={this.state.isCreated}
          onHide={() => this.dismissCreateModal()}
        />
        <ACPackagedUnitEditModal
          ac={item}
          show={isShowEditModal}
          isEdited={this.state.isEdited}
          onHide={() => this.dismissEditModal()}
        />
        <ACPackagedUnitDuplicateModal
          item={item}
          show={isShowDuplicateModal}
          title={t('copy a/c packaged unit')}
          label={t('new a/c packaged unit name')}
          isDuplicated={this.state.isDuplicated}
          onHide={() => !isDuplicating && this.dismissDuplicateModal()}
        />
        <Alert
          show={isShowDeleteModal}
          onHide={() => !isDeleting && this.dismissDeleteModal()}
          title={t('delete a/c packaged unit')}
          body={`${t('do you want to delete')} ${item ? item.name : ''}?`}
          errorMessage={getErrorValue(errorMessage, 'detail')}
          primaryButtonTitle={isDeleting ? t('deleting') : t('delete')}
          primaryButtonStyle={'danger'}
          primaryButtonEnabled={!isDeleting}
          onPrimaryButtonClick={() => this.props.deleteAC(item)}
        />
        <h1>{t('a/c packaged unit')}</h1>
        <div style={{ backgroundColor: 'white' }}>
          <div className="filter-box" style={{ display: 'flex', padding: 20, marginBottom: 20 }}>
            <input
              id="air-conditioning-package-unit-search-name"
              className="text-search"
              style={{ width: 240, height: 36, margin: 10 }}
              type="text"
              onChange={event => this.updateSortFilter(event.target.value, null, categoryOption)}
              placeholder={t('search name')}
            />
            <Select
              className="react-select-container"
              classNamePrefix="react-select"
              style={{ width: 160, margin: 10 }}
              value={categoryOption}
              placeholder={t('category')}
              onChange={option => this.updateSortFilter(filtered, null, option)}
              options={[
                { value: 'all', label: t('all') },
                { value: 'public', label: t('public') },
                { value: 'private', label: t('private') },
              ]}
            />
          </div>
          <DataTable
            columns={this.columns}
            data={this.props.acList}
            onActionEdit={item => this.setState({ isShowEditModal: true, selectedItem: item.original, isEdited: false })}
            onActionDuplicate={item => this.setState({ isShowDuplicateModal: true, selectedItem: item.original, isDuplicated: false })}
            onActionDelete={item => this.setState({ isShowDeleteModal: true, selectedItem: item.original })}
            filteredCount={filteredCount}
            isLoading={isLoading}
            onFetchData={state => this.updateSortFilter(filtered, state, categoryOption)}
          />
        </div>
      </Layout>
    );
  }
}

const mapStateToProps = state => ({
  acList: state.ac.packagedUnit.orderedIds.map(id => state.ac.packagedUnit.objects[id]),
  filteredCount: state.ac.packagedUnit.filteredCount,
  isLoading: state.ac.packagedUnit.isLoading,
  isCreating: state.ac.packagedUnit.isCreating,
  isEditing: state.ac.packagedUnit.isEditing,
  isDeleting: state.ac.packagedUnit.isDeleting,
  isDuplicating: state.ac.packagedUnit.isDuplicating,
  errorMessage: state.ac.packagedUnit.errorMessage,
});

const mapDispatchToProps = dispatch => ({
  listAC: (pageSize, page, sorted, filtered, isPublic) =>
    defaultRejectHandler(
      dispatch(listACPackagedUnit(pageSize, page, sorted, filtered, isPublic))
    ),
  deleteAC: ac => defaultRejectHandler(dispatch(deleteACPackagedUnit(ac.id))),
  clearError: () => dispatch(clearErrorACPackagedUnit()),
});

export default translate('translations')(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(ACPackagedUnitPage),
);



// WEBPACK FOOTER //
// ./src/pages/ac_packaged_unit/ACPackagedUnitPage.js